import { useNavigate } from "react-router-dom";
import { ArrowRight, Layers, Pencil, Trash2 } from "lucide-react";
import { PATHS } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CATEGORY_META } from "../schemas/custom-framework.schema";
import { resolveLogoUrl } from "../stores/custom-framework.store";

export function FrameworkCard({ framework, onEdit, onDelete }) {
  const navigate = useNavigate();
  const builderId = PATHS.CUSTOM_FRAMEWORK_BUILDER.replace(
    ":id",
    framework._id,
  );
  const metrics = framework.metrics ?? [];
  const logoUrl = resolveLogoUrl(framework.logoUrl);

  const counts = {};
  for (const m of metrics) counts[m.category] = (counts[m.category] ?? 0) + 1;

  return (
    <div className="flex h-full flex-col rounded-lg border border-border bg-card">
      <div className="flex flex-1 flex-col gap-3 p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="h-10 w-10 shrink-0 rounded-lg border border-border object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Layers className="h-5 w-5" />
              </div>
            )}
            <p className="min-w-0 flex-1 truncate font-semibold text-foreground">
              {framework.name}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => onEdit(framework)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => onDelete(framework)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <p className="line-clamp-2 min-h-[5rem] whitespace-pre-wrap text-sm text-muted-foreground">
          {framework.description}
        </p>
      </div>

      {/* Metrics summary */}
      <div className="border-t border-border px-5 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {CATEGORY_META.filter((c) => c.key !== "Other").map(
              ({ key, label, bg, text }) => (
                <Badge key={key} className={`${bg} ${text} gap-1 border-0`}>
                  <span className="font-semibold">{label}</span>
                  <span>{counts[key] ?? 0}</span>
                </Badge>
              ),
            )}
          </div>
          <Button
            type="button"
            size="sm"
            className="shrink-0"
            onClick={() => navigate(builderId)}
          >
            Manage Metrics
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
